import * as Sentry from '@sentry/vue';
import {QueryClient, VueQueryPlugin} from '@tanstack/vue-query';
import {createBootstrap} from 'bootstrap-vue-next';
import {createPinia} from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import {createApp} from 'vue';
import {pageTitle} from 'vue-page-title';
import type {RouteRecordRaw} from 'vue-router';
import VueSweetalert2 from 'vue-sweetalert2';
import Vue3Toastify from 'vue3-toastify';

import App from '@/App.vue';
import type {TenantConfig} from '@/shared/features/tenants/tenant.model';
import {useTenantConfigStore} from '@/shared/stores/tenantConfig.store';

import {createTenantRouter} from './router';

export function createTenantApp({
   tenantConfig,
   routes,
}: {
   tenantConfig: TenantConfig;
   routes: RouteRecordRaw[];
}) {
   const router = createTenantRouter(routes);

   const queryClient = new QueryClient({
      defaultOptions: {
         queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
         },
      },
   });

   const app = createApp(App);
   const store = createPinia();
   store.use(piniaPluginPersistedstate);

   if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
         app,
         dsn: import.meta.env.VITE_SENTRY_DSN,
         sendDefaultPii: true,
         tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
            ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
            : undefined,
         replaysSessionSampleRate: import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE
            ? parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE)
            : undefined,
         replaysOnErrorSampleRate: import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE
            ? parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE)
            : undefined,
      });
   }

   app.use(store);
   app.use(router);
   app.use(Vue3Toastify);
   app.use(VueQueryPlugin, {queryClient});
   app.use(VueSweetalert2);
   app.use(createBootstrap());
   app.use(pageTitle({suffix: ` | ${tenantConfig.name}`, router}));

   const tenantConfigStore = useTenantConfigStore();
   tenantConfigStore.resolveTenant().finally(() => {
      app.mount('#app');
   });
}
